import { Link } from '@tanstack/react-router'
import { Building2, Keyboard, LogOut, User } from 'lucide-react'
import { useMe } from '@/api/queries'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Kbd } from '@/components/ui/kbd'
import { useUser } from '@/hooks/useUser'
import { userDisplayName, userInitials } from '@/lib/user-display'

interface UserMenuProps {
  /**
   * Invoked when the user clicks the "Keyboard shortcuts" item. The dialog
   * state lives in `AppShell`, so this is the canonical handle to open it —
   * synthesising a `?` keydown previously didn't work because the global
   * listener lives on `document` and Radix menu items consume the event.
   */
  onShowShortcuts?: () => void
}

export function UserMenu({ onShowShortcuts }: UserMenuProps = {}) {
  const { isAuthenticated, user, signOut } = useUser()
  const { data: me } = useMe()
  // Render the menu whenever the user is authenticated, even before claims
  // have settled — a network blip on the claims fetch must NOT leave the
  // user stranded without a logout affordance.
  if (!isAuthenticated) return null

  const menuUser = {
    email: user?.email || me?.email || '',
    name: me?.name || user?.name || null,
    avatarUrl: me?.avatar_url || user?.avatarUrl || null,
  }
  const avatarLabel = userInitials(user || me ? menuUser : null)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-2"
          data-testid="user-menu-trigger"
          aria-label="Open user menu"
        >
          <span className="bg-secondary text-secondary-foreground inline-flex size-7 items-center justify-center rounded-full text-xs font-medium">
            {menuUser.avatarUrl ? (
              <img
                src={menuUser.avatarUrl}
                alt=""
                className="size-full rounded-full object-cover"
                referrerPolicy="no-referrer"
              />
            ) : (
              avatarLabel
            )}
          </span>
          {user || me ? (
            <span className="hidden text-sm sm:inline">
              {userDisplayName(menuUser)}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {user || me ? (
          <>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-0.5">
                <span className="text-sm font-medium">
                  {userDisplayName(menuUser)}
                </span>
                <span className="text-muted-foreground text-xs">
                  {menuUser.email}
                </span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        ) : null}
        <DropdownMenuItem asChild data-testid="user-menu-account">
          <Link to="/account">
            <User />
            Account
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid="user-menu-workspaces">
          <Link to="/workspaces">
            <Building2 />
            Workspaces
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid="user-menu-shortcuts"
          onClick={() => onShowShortcuts?.()}
        >
          <Keyboard />
          <span className="flex-1">Keyboard shortcuts</span>
          <Kbd className="ml-auto">?</Kbd>
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="user-menu-signout"
          onClick={() => signOut()}
        >
          <LogOut />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
